import FleetModal from '@/libs/antd/components/Modal';
import { formatCurrency } from '@/libs/antd/formater';
import { OfficeOwnershipStatus } from '@/libs/fleet-api/enums';
import {
	getVisitReportOfficePdfDetail,
	getVisitReportSitePdfDetail,
} from '@/libs/fleet-api/services/visit-reports';
import { useQuery } from '@tanstack/react-query';
import { Col, Row, Image as Img } from 'antd';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { FC, useState } from 'react';

const downloadPDF = async (el: HTMLElement, filename: string) => {
	const canvas = await html2canvas(el, {
		allowTaint: true,
		logging: true,
		useCORS: true,
	});

	const img = new Image();
	img.src = canvas.toDataURL('image/jpeg', 1.0);
	await img.decode();

	img.style.width = '100%';
	img.style.display = 'block';
	el.appendChild(img);

	const orientation = img.height > img.width ? 'portrait' : 'landscape';
	const pdf = new jsPDF(orientation, 'px', [img.height, img.width]);
	pdf.addImage(canvas, 'JPEG', 0, 0, img.width, img.height, 'px', 'SLOW');
	pdf.save(filename);
	img.remove();
};

const GenerateLKPDFModal: FC<{
	modal: GenerateLKPDFModalState;
	onClose: () => void;
}> = ({ modal, onClose }) => {
	const { data: officeData } = useQuery({
		queryKey: ['getVisitReportOfficePdfDetail', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return;
			return getVisitReportOfficePdfDetail(id);
		},
		enabled: Boolean(modal && modal.prospectId && modal.type === 'lk-office'),
	});

	const { data: siteData } = useQuery({
		queryKey: ['getVisitReportSitePdfDetail', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getVisitReportSitePdfDetail(id);
		},
		enabled: Boolean(modal && modal.prospectId && modal.type === 'lk-site'),
	});

	const handleOpenChange = (isOpen: boolean) => {
		if (!isOpen) return;
		const printArea = document.getElementById(modal!.type);

		if (!printArea) return;
		downloadPDF(printArea, `${modal!.type}-${Date.now()}.pdf`);
		onClose();
	};

	const renderOffice = () => {
		const {
			mainInformation,
			warehouse,
			surveyInformation,
			technicalAspect,
			marketingAspect,
			collaterals,
			collateralNotes,
		} = officeData?.data ?? {};

		const attendees =
			technicalAspect?.attendees && technicalAspect?.attendees.length > 3
				? technicalAspect?.attendees?.splice(
						3,
						technicalAspect.attendees.length,
					)
				: [];

		const officeSubmitDate =
			officeData?.data?.approvalHistories?.[0]?.approvalDate ?? '-';

		return (
			<div
				id="lk-office"
				className="print"
			>
				<header>LAPORAN KUNJUNGAN KANTOR</header>
				<table>
					<colgroup>
						<col width={'20%'} />
						<col width={'25%'} />
						<col width={'5%'} />
						<col width={'20%'} />
						<col width={'12%'} />
						<col width={'6%'} />
						<col width={'12%'} />
					</colgroup>

					<tbody>
						<tr>
							<td>Nama Calon Debitur</td>
							<td className="insert">: {mainInformation?.customerName}</td>
							<td width={100} />
							<td>Tanggal Kunjungan</td>
							<td
								className="insert"
								colSpan={3}
							>
								:{' '}
								{surveyInformation?.surveyOfficeDate &&
									dayjs(surveyInformation.surveyOfficeDate).format(
										'DD/MM/YYYY',
									)}
							</td>
						</tr>

						<tr>
							<td>Alamat Kantor</td>
							<td className="insert">: {surveyInformation?.officeAddress}</td>
							<td width={100} />
							<td>Lokasi Kunjungan</td>
							<td
								className="insert"
								colSpan={3}
							>
								: {surveyInformation?.officeAddress}
							</td>
						</tr>

						<tr>
							<td>Alamat Workshop/Gudang</td>
							<td className="insert">: {warehouse?.warehouseAddress}</td>
							<td width={100} />
							<td>Nama Fleet Relationship Manager (FRM)</td>
							<td
								className="insert"
								colSpan={3}
							>
								: {mainInformation?.frm?.value}
							</td>
						</tr>

						<tr>
							<td>No. Telp Kantor</td>
							<td className="insert">: {warehouse?.warehousePhoneNo}</td>
							<td width={100} />
							<td rowSpan={3}>Pihak Yang Hadir</td>
							<td className="insert">
								: {technicalAspect?.attendees?.[0]?.name}
							</td>
							<td width={100}>Jabatan:</td>
							<td className="insert">
								: {technicalAspect?.attendees?.[0]?.position}
							</td>
						</tr>

						<tr>
							<td>Bidang Usaha</td>
							<td className="insert">
								: {mainInformation?.industrySector?.sectorLevel5}
							</td>
							<td width={100} />
							<td className="insert">
								: {technicalAspect?.attendees?.[1]?.name}
							</td>
							<td width={100}>Jabatan:</td>
							<td className="insert">
								: {technicalAspect?.attendees?.[1]?.position}
							</td>
						</tr>

						<tr>
							<td>Group usaha</td>
							<td className="insert">
								: {mainInformation?.customerGroup?.value}
							</td>
							<td width={100} />
							<td className="insert">
								: {technicalAspect?.attendees?.[2]?.name}
							</td>
							<td width={100}>Jabatan:</td>
							<td className="insert">
								: {technicalAspect?.attendees?.[2]?.name}
							</td>
						</tr>

						{attendees.map((attendee) => (
							<tr key={attendee.id}>
								<td colSpan={3} />
								<td width={100} />
								<td className="insert">: {attendee.name}</td>
								<td width={100}>Jabatan:</td>
								<td className="insert">: {attendee.position}</td>
							</tr>
						))}

						<tr>
							<td
								colSpan={7}
								className="underline"
							/>
						</tr>
						<tr>
							<td colSpan={7} />
						</tr>
					</tbody>
				</table>

				<table>
					<colgroup>
						<col width={'3%'} />
						<col width={'5%'} />
						<col />
						<col />
						<col />
						<col />
						<col width={'10%'} />
						<col width={'20%'} />
					</colgroup>
					<tbody>
						<tr>
							<td>
								<b>1</b>
							</td>
							<td colSpan={5}>
								<b>Observasi Kantor - Aspek Teknis</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">a</td>
							<td>Tahun memulai usaha</td>
							<td className="insert">
								:{' '}
								{technicalAspect?.startedBusinessYear &&
									dayjs(technicalAspect.startedBusinessYear).format('YYYY')}
							</td>
							<td colSpan={2}>; dimana usaha sebelumnya yaitu (jika ada)</td>
							<td
								colSpan={2}
								className="insert"
							>
								: {technicalAspect?.previousBusiness}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">b</td>
							<td>Tanggal Pendirian Perusahaan</td>
							<td className="insert">
								:{' '}
								{technicalAspect?.companyEstablishmentDate &&
									dayjs(technicalAspect.companyEstablishmentDate).format(
										'DD/MM/YYYY',
									)}
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">c</td>
							<td>Jumlah kantor cabang saat ini</td>
							<td className="insert">
								: {technicalAspect?.numberCurrentBranch}
							</td>
							<td colSpan={2}>; diantaranya di kota</td>
							<td
								colSpan={2}
								className="insert"
							>
								: {technicalAspect?.officePostalCode?.cityName}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">d</td>
							<td>Status Kepemilikan Kantor</td>
							<td className="insert">
								: {technicalAspect?.officeOwnershipStatus?.value}
							</td>
							<td colSpan={2}>
								; jika milik perusahaan/owner, mulai menempati sejak
							</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{(technicalAspect?.officeOwnershipStatus?.id ===
									OfficeOwnershipStatus['Milik Perusahaan'] ||
									technicalAspect?.officeOwnershipStatus?.id ===
										OfficeOwnershipStatus['Milik Owner']) &&
									technicalAspect.officeOccupationDate &&
									dayjs(technicalAspect.officeOccupationDate).format('YYYY')}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika sewa, periode sewanya</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{technicalAspect?.officeOwnershipStatus?.id ===
									OfficeOwnershipStatus.Sewa &&
									technicalAspect.officeRentalStartDate &&
									technicalAspect.officeRentalEndDate &&
									`${dayjs(technicalAspect.officeRentalStartDate).format('DD/MM/YYYY')} s/d ${dayjs(technicalAspect.officeRentalEndDate).format('DD/MM/YYYY')}`}
							</td>
						</tr>

						{/* <tr>
							<td colSpan={4} />
							<td colSpan={3}>; jika sewa, biaya sewa per tahun sebesar</td>
							<td className="insert">
								:{' '}
								{technicalAspect?.officeAnnualRentalFee
									? formatCurrency(technicalAspect.officeAnnualRentalFee)
									: 'Rp.'}
							</td>
						</tr> */}

						<tr>
							<td />
							<td align="center">e</td>
							<td>Jumlah karyawan saat ini</td>
							<td className="insert">: {technicalAspect?.officeHeadcount}</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">f</td>
							<td>Jumlah Unit yang dimiliki saat ini</td>
							<td className="insert">
								:{' '}
								{technicalAspect?.units?.reduce(
									(acc, curr) => acc + curr.unitAmount,
									0,
								)}
							</td>
							<td colSpan={2}>; diantaranya yaitu</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{technicalAspect?.units?.[0] &&
									`${technicalAspect?.units?.[0].unitAmount} unit ${technicalAspect?.units?.[0].unitBrandDetail}`}
							</td>
						</tr>

						<tr>
							<td colSpan={6} />
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{technicalAspect?.units?.[1] &&
									`${technicalAspect?.units?.[1]?.unitAmount} unit ${technicalAspect?.units?.[1]?.unitBrandDetail}`}
							</td>
						</tr>

						{technicalAspect?.units
							?.filter((_, i) => i >= 2)
							?.map((unit, i) => (
								<tr key={i}>
									<td colSpan={6} />
									<td
										colSpan={2}
										className="insert"
									>
										: {`${unit?.unitAmount} unit ${unit?.unitBrandDetail}`}
									</td>
								</tr>
							))}

						<tr>
							<td />
							<td align="center">g</td>
							<td>Info Tambahan (jika ada)</td>
							<td
								colSpan={5}
								className="insert"
								style={{ height: 60 }}
							>
								: {technicalAspect?.technicalAspectNotes}
							</td>
						</tr>

						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td>
								<b>2</b>
							</td>
							<td colSpan={5}>
								<b>Observasi Kantor - Aspek Pemasaran</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">a</td>
							<td>Produk/Jasa yang dipasarkan</td>
							<td
								colSpan={5}
								className="insert"
							>
								: {marketingAspect?.productOffer}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">b</td>
							<td>Metode Penjualan</td>
							<td className="insert">
								:{' '}
								{marketingAspect?.salesMethods
									?.map((sale) => sale.salesMethod.value)
									?.join(', ')}
							</td>
							<td colSpan={2}>; jika Direct, sebagai berikut detialnya</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{marketingAspect?.salesMethods
									?.filter((sale) => sale.salesMethod.value === 'Direct')
									?.map((sale) => sale.methodDetail)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika Pihak Ketiga Sebutkan</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{marketingAspect?.salesMethods
									?.filter((sale) => sale.salesMethod.value === 'Pihak Ketiga')
									?.map((sale) => sale.methodDetail)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika Market Place, sebutkan</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{marketingAspect?.salesMethods
									?.filter((sale) => sale.salesMethod.value === 'Marketplace')
									?.map((sale) => sale.methodDetail)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">c</td>
							<td>Sistem Pembayaran</td>
							<td className="insert">
								:{' '}
								{marketingAspect?.paymentSystem
									?.map((payment) => payment.paymentSystem.value)
									?.join(', ')}
							</td>
							<td colSpan={2}>; dengan porsi sebagai berikut</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{marketingAspect?.paymentSystem
									?.map(
										(payment) =>
											`${payment.paymentSystem.value} (${payment.portionPercentage}%)`,
									)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">d</td>
							<td>Apakah terdapat usaha lain</td>
							<td className="insert">
								:{' '}
								{marketingAspect?.isOtherBusiness
									? 'Ada'
									: marketingAspect?.isOtherBusiness === false
										? 'Tidak'
										: ''}
							</td>
							<td colSpan={3}>
								; jika ada sebutkan, dan berapa % porsi terhadap sales?
							</td>
							<td className="insert">
								:{' '}
								{marketingAspect?.otherBusinessPortion &&
									`${marketingAspect.otherBusinessPortion}%`}
								.
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">e</td>
							<td>Kompetitor</td>
							<td className="insert">
								:{' '}
								{marketingAspect?.competitors
									?.map((competitor) => competitor.name)
									.join(', ')}
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">f</td>
							<td colSpan={6}>Nama Bouwheer Perusahaan / Perorangan</td>
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Nama Bouwheer/Customer</th>
											<th>Nama Pekerjaan</th>
											<th>Lokasi Pekerjaan</th>
											<th>Sistem Pembayaran</th>
											<th>TOP Pembayaran</th>
										</tr>
									</thead>
									<tbody>
										{marketingAspect?.bouwheers?.length
											? marketingAspect.bouwheers.map((bouwheer, i) => (
													<tr key={i}>
														<td>{i + 1}</td>
														<td className="insert">{bouwheer.bouwheer.name}</td>
														<td className="insert">{bouwheer.jobTitle}</td>
														<td className="insert">{bouwheer.jobLocation}</td>
														<td className="insert">-</td>
														<td className="insert">{bouwheer.termOfPayment}</td>
													</tr>
												))
											: Array(5)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">g</td>
							<td>Info Tambahan (jika ada)</td>
							<td
								colSpan={5}
								className="insert"
								style={{ height: 60 }}
							>
								: {marketingAspect?.marketingAspectNotes}
							</td>
						</tr>

						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td>
								<b>3</b>
							</td>
							<td colSpan={5}>
								<b>Observasi Workshop/Gudang - Aspek Teknis</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td colSpan={5}>
								<b>Kondisi Workskhop/Gudang (jika ada)</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">a</td>
							<td>Status Kepemilikan Workshop/Gudang</td>
							<td className="insert">
								: {warehouse?.warehouseOwnershipStatus?.value}
							</td>
							<td colSpan={2}>
								; jika milik perusahaan/owner, mulai menempati sejak
							</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.warehouseOccupationDate &&
									dayjs(warehouse.warehouseOccupationDate).format('YYYY')}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika sewa, periode sewanya</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.warehouseOwnershipStatus?.id ===
									OfficeOwnershipStatus.Sewa &&
									warehouse?.warehouseRentalStartDate &&
									warehouse?.warehouseRentalEndDate &&
									`${warehouse.warehouseRentalStartDate} s/d ${warehouse.warehouseRentalEndDate}`}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika sewa, biaya sewa per tahun sebesar</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.warehouseOwnershipStatus?.id ===
									OfficeOwnershipStatus.Sewa &&
								warehouse?.warehouseAnnualRentalFee
									? formatCurrency(warehouse.warehouseAnnualRentalFee)
									: 'Rp.'}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">b</td>
							<td>No Telp Workshop/Gudang</td>
							<td className="insert">
								: {warehouse?.warehouseSize && `${warehouse?.warehousePhoneNo}`}
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">c</td>
							<td>Luas Workshop/Gudang</td>
							<td className="insert">
								:{' '}
								{warehouse?.warehouseSize && `${warehouse?.warehouseSize} (m2)`}
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">d</td>
							<td>Kapasitas Workshop/Gudang</td>
							<td className="insert">: {warehouse?.warehouseCapacity}</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">e</td>
							<td>Jumlah Karyawan</td>
							<td className="insert">: {warehouse?.warehouseHeadcount}</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">f</td>
							<td>Keahlian Khusus</td>
							<td className="insert">: {warehouse?.specializedExpertise}</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td colSpan={5}>
								<b>Ketersedian Sparepart</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table
									className="bordered"
									style={{ textAlign: 'center' }}
								>
									<thead>
										<tr>
											<th>No</th>
											<th>Nama Item</th>
											<th>Jumlah</th>
										</tr>
									</thead>
									<tbody>
										{warehouse?.spareParts.length
											? warehouse?.spareParts.map((spare, i) => (
													<tr key={spare.id}>
														<td>{i + 1}</td>
														<td className="insert">{spare.name}</td>
														<td className="insert">{spare.quantity}</td>
													</tr>
												))
											: Array(3)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td>{i + 1}</td>
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr>

						<tr>
							<td />
							<td colSpan={5}>
								<b>Kondisi Barang Persediaan</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table
									className="bordered"
									style={{ textAlign: 'center' }}
								>
									<thead>
										<tr>
											<th>No</th>
											<th>Jenis Barang Persediaan</th>
											<th>Detail Barang Persediaan</th>
											<th>Jumlah Persediaan Saat Kunjungan Usaha</th>
										</tr>
									</thead>
									<tbody>
										{warehouse?.inventories.length
											? warehouse?.inventories.map((data, i) => (
													<tr key={data.id}>
														<td>{i + 1}</td>
														<td className="insert">{data.type.value}</td>
														<td className="insert">{data.name}</td>
														<td className="insert">{data.quantity}</td>
													</tr>
												))
											: Array(5)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td>{i + 1}</td>
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr>

						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td colSpan={2} />
							<td>Kepemilikan bahan</td>
							<td className="insert">
								: {warehouse?.materialOwnershipType?.value}
							</td>
							<td colSpan={2}>; Jika Konsinyasi, Berapa %? :</td>
							<td
								colSpan={2}
								className="insert"
							>
								: {warehouse?.materialOwnershipPortion}%
							</td>
						</tr>

						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td />
							<td colSpan={5}>
								<b>List Supplier Pareto</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Nama Supplier</th>
											<th>No. Telepon Perusahaan</th>
											<th>Pembelian/Bulan (Rp.)</th>
											<th>Sistem Pembayaran</th>
											<th>Kendala (jika ada)</th>
										</tr>
									</thead>
									<tbody>
										{warehouse?.paretoSuppliers?.length
											? warehouse.paretoSuppliers.map((supplier, i) => (
													<tr key={i}>
														<td>{i + 1}</td>
														<td className="insert">{supplier.supplierName}</td>
														<td className="insert">{supplier.phoneNumber}</td>
														<td className="insert">
															{formatCurrency(supplier.monthlyPurchase)}
														</td>
														<td className="insert">{supplier.paymentSystem}</td>
														<td className="insert">{supplier.constraint}</td>
													</tr>
												))
											: Array(3)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr>

						<tr>
							<td />
							<td colSpan={5}>
								<b>List Piutang Pareto</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Nama Bouwheer</th>
											<th>Nama Pekerjaan</th>
											<th>No Telp Perusahaan</th>
											<th>Nilai Piutang (Rp)</th>
											<th>Umur Piutang Terlama (Hari)</th>
											<th>Kendala (Jika Ada)</th>
										</tr>
									</thead>
									<tbody>
										{warehouse?.paretoReceivables?.length
											? warehouse.paretoReceivables.map((data, i) => (
													<tr key={i}>
														<td>{i + 1}</td>
														<td className="insert">{data.bouwheerName}</td>
														<td className="insert">{data.jobName}</td>
														<td className="insert">{data.phoneNumber}</td>
														<td className="insert">
															{formatCurrency(data.receivableValue)}
														</td>
														<td className="insert">
															{data.oldestReceivableAge}
														</td>
														<td className="insert">{data.constraint}</td>
													</tr>
												))
											: Array(3)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr>

						<tr>
							<td colSpan={2} />
							<td>Informasi Tambahan</td>
							<td
								colSpan={5}
								className="insert"
							>
								: {warehouse?.warehouseNotes}
							</td>
						</tr>

						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td>
								<b>4</b>
							</td>
							<td colSpan={5}>
								<b>Observasi Agunan</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Jenis Agunan</th>
											<th>Merk/Tipe serta Kapasitas</th>
											<th>Tahun Perakitan</th>
											<th>Kepemilikan</th>
											<th>Hour/Meter</th>
											<th>Kondisi Fisik</th>
											<th>Kondisi Fungsi</th>
											<th>Keterangan (jika ada)</th>
										</tr>
									</thead>
									<tbody>
										{collaterals?.length
											? collaterals.map((collateral, i) => (
													<tr key={i}>
														<td className="insert">{i + 1}</td>
														<td className="insert">
															{collateral.collateralType}
														</td>
														<td className="insert">
															{collateral.brandTypeAndCapacity}
														</td>
														<td className="insert">
															{collateral.yearOfAssembly}
														</td>
														<td className="insert">{collateral.ownership}</td>
														<td className="insert">{collateral.hoursMeters}</td>
														<td className="insert">
															{collateral.physicalCondition}
														</td>
														<td className="insert">
															{collateral.functionCondition}
														</td>
														<td className="insert">
															{collateral.additionalInformation}
														</td>
													</tr>
												))
											: Array(8)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr>

						<tr>
							<td colSpan={2} />
							<td>Informasi Tambahan</td>
							<td
								colSpan={5}
								className="insert"
							>
								: {collateralNotes}
							</td>
						</tr>

						{/* <tr>
							<td />
							<td colSpan={5}>
								<b>Kondisi Barang Persediaan (jika ada)</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">a</td>
							<td>Jenis Barang Persediaan</td>
							<td className="insert">
								:{' '}
								{warehouse?.inventories
									?.map((inventory) => inventory.type.value)
									?.join(', ')}
							</td>
							<td colSpan={2}>; jika Barang jadi, sebutkan</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.inventories
									?.filter(
										(inventory) => inventory.type.value === 'Barang Jadi',
									)
									?.map((inventory) => inventory.name)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika Barang Setengah Jadi, sebutkan</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.inventories
									?.filter(
										(inventory) =>
											inventory.type.value === 'Barang Setengah Jadi',
									)
									?.map((inventory) => inventory.name)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td colSpan={4} />
							<td colSpan={2}>; jika Bahan Baku, sebutkan</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.inventories
									?.filter((inventory) => inventory.type.value === 'Bahan Baku')
									?.map((inventory) => inventory.name)
									?.join(', ')}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">b</td>
							<td>Jumlah persediaan saat kunjungan Usaha</td>
							<td className="insert">: (unit)</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">c</td>
							<td>Kepemilikan Bahan</td>
							<td className="insert">
								: {warehouse?.materialOwnershipType.value}
							</td>
							<td colSpan={2}>; jika Konsinyasi, barapa %?</td>
							<td
								colSpan={2}
								className="insert"
							>
								:{' '}
								{warehouse?.materialOwnershipPortion &&
									`${warehouse?.materialOwnershipPortion}%`}
							</td>
						</tr>

						<tr>
							<td />
							<td align="center">d</td>
							<td colSpan={6}>List Supplier Pareto</td>
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Nama Supplier</th>
											<th>No. Telepon Perusahaan</th>
											<th>Pembelian/Bulan (Rp.)</th>
											<th>Sistem Pembayaran</th>
											<th>Kendala (jika ada)</th>
										</tr>
									</thead>
									<tbody>
										{warehouse?.paretoSuppliers?.length
											? warehouse.paretoSuppliers.map((supplier, i) => (
													<tr key={i}>
														<td>{i + 1}</td>
														<td className="insert">{supplier.supplierName}</td>
														<td className="insert">{supplier.phoneNumber}</td>
														<td className="insert">
															{supplier.monthlyPurchase}
														</td>
														<td className="insert">{supplier.paymentSystem}</td>
														<td className="insert">{supplier.constraint}</td>
													</tr>
												))
											: Array(3)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr> */}

						{/* <tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td>
								<b>4</b>
							</td>
							<td colSpan={5}>
								<b>Observasi Workshop/Gudang - Aspek Piutang Dagang</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">a</td>
							<td>Total Nilai Piutang</td>
							<td className="insert">: Rp.</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td align="center">b</td>
							<td colSpan={6}>List Piutang Pareto</td>
						</tr>

						<tr>
							<td colSpan={2} />
							<td colSpan={6}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Nama Bouwheer</th>
											<th>Nama Pekerjaan</th>
											<th>No. Telepon Perusahaan</th>
											<th>Nilai Piutang (Rp. Ribu)</th>
											<th>Umur Piutang Terlama (Hari)</th>
											<th>Kendala (jika ada)</th>
										</tr>
									</thead>
									<tbody>
										{Array(3)
											.fill(null)
											.map((_, i) => (
												<tr key={i}>
													<td />
													<td className="insert" />
													<td className="insert" />
													<td className="insert" />
													<td className="insert" />
													<td className="insert" />
													<td className="insert" />
												</tr>
											))}
									</tbody>
								</table>
							</td>
						</tr>
						<tr>
							<td />
							<td align="center">g</td>
							<td>Info Tambahan (jika ada)</td>
							<td
								colSpan={5}
								className="insert"
								style={{ height: 60 }}
							>
								:
							</td>
						</tr>

						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td>
								<b>5</b>
							</td>
							<td colSpan={5}>
								<b>Observasi Agunan (jika ada)</b>
							</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td />
							<td colSpan={2}>Detail Unit yang akan diagunkan</td>
							<td colSpan={5}>
								<table className="bordered">
									<colgroup>
										<col width={100} />
										<col />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>No</th>
											<th>Jenis Agunan</th>
											<th>Merk/Tipe serta Kapasitas</th>
											<th>Tahun Perakitan</th>
											<th>Kepemilikan</th>
											<th>Hour/Meter</th>
											<th>Kondisi Fisik</th>
											<th>Kondisi Fungsi</th>
											<th>Keterangan (jika ada)</th>
										</tr>
									</thead>
									<tbody>
										{collaterals?.length
											? collaterals.map((collateral, i) => (
													<tr key={i}>
														<td className="insert">{i + 1}</td>
														<td className="insert">
															{collateral.collateralType}
														</td>
														<td className="insert">
															{collateral.brandTypeAndCapacity}
														</td>
														<td className="insert">
															{collateral.yearOfAssembly}
														</td>
														<td className="insert">{collateral.ownership}</td>
														<td className="insert">{collateral.hoursMeters}</td>
														<td className="insert">
															{collateral.physicalCondition}
														</td>
														<td className="insert">
															{collateral.functionCondition}
														</td>
														<td className="insert">
															{collateral.additionalInformation}
														</td>
													</tr>
												))
											: Array(8)
													.fill(null)
													.map((_, i) => (
														<tr key={i}>
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
															<td className="insert" />
														</tr>
													))}
									</tbody>
								</table>
							</td>
						</tr> */}

						<tr>
							<td colSpan={8} />
						</tr>
						<tr>
							<td
								colSpan={8}
								className="underline"
							/>
						</tr>
						<tr>
							<td colSpan={8} />
						</tr>

						<tr>
							<td colSpan={2}>Tanggal</td>
							<td
								colSpan={2}
								className="insert"
							>
								: {officeSubmitDate}
							</td>
							<td colSpan={4} />
						</tr>

						<tr>
							<td colSpan={2}>Nama Fleet Relation Manager </td>
							<td
								colSpan={2}
								className="insert underline"
							>
								: {mainInformation?.frm?.value}
							</td>
							<td />
							<td colSpan={2}>Nama Fleet Business Dep</td>
							<td className="insert underline">
								: {mainInformation?.fbdh?.value}
							</td>
						</tr>
						<tr>
							<td
								colSpan={2}
								style={{
									height: 100,
									verticalAlign: 'middle',
								}}
							>
								Tandatangan
							</td>
							<td
								colSpan={2}
								className="insert underline"
							></td>
							<td />
							<td
								colSpan={2}
								style={{ verticalAlign: 'middle' }}
							>
								Tandatangan
							</td>
							<td className="insert underline"></td>
						</tr>
						<tr>
							<td
								colSpan={8}
								className="underline"
							/>
						</tr>
					</tbody>
				</table>

				<header style={{ marginBlock: 20 }}>
					LAMPIRAN FOTO HASIL KUNJUNGAN
				</header>
				<Row
					gutter={20}
					justify="center"
				>
					{technicalAspect?.photos?.map((data) => {
						return (
							<Col
								key={data.id}
								span={6}
							>
								<Img
									src={data.photo.url}
									preview={false}
									crossOrigin="anonymous"
								/>
							</Col>
						);
					})}
				</Row>
			</div>
		);
	};

	const renderSite = () => {
		const { siteReport, surveyInformation, mainInformation } =
			siteData?.data ?? {};

		const siteSubmitDate =
			siteData?.data?.approvalHistories?.[0]?.approvalDate ?? '-';

		return (
			<div
				id="lk-site"
				className="print"
			>
				<header>LAPORAN KUNJUNGAN SITE</header>
				<table style={{ marginBottom: 20 }}>
					<colgroup>
						<col width={'20%'} />
						<col width={'5%'} />
						<col width={'30%'} />
						<col width={'15%'} />
						<col width={'30%'} />
					</colgroup>
					<tbody>
						<tr>
							<td>Nama Calon Debitur</td>
							<td />
							<td className="insert">: {mainInformation?.customerName}</td>
							<td colSpan={2} />
						</tr>
						<tr>
							<td>Alamat Site</td>
							<td />
							<td className="insert">: {siteReport?.visitSiteAddress}</td>
							<td colSpan={2} />
						</tr>
						<tr>
							<td>Tanggal Kunjungan Site</td>
							<td />
							<td className="insert">
								:{' '}
								{surveyInformation?.surveySiteDate &&
									dayjs(surveyInformation.surveySiteDate).format('DD/MM/YYYY')}
							</td>
							<td colSpan={2} />
						</tr>
						<tr>
							<td>Nama Fleet Relationship Manager</td>
							<td />
							<td className="insert">: {mainInformation?.frm?.value}</td>
							<td colSpan={2} />
						</tr>
						{siteReport?.attendees.length ? (
							siteReport?.attendees.map((attendee, index) => {
								return (
									<tr key={attendee.id}>
										<td>{!index ? 'Pihak Yang Hadir' : ''}</td>
										<td />
										<td className="insert">: {attendee.name}</td>
										<td align="center">Jabatan</td>
										<td className="insert">: {attendee.position}</td>
									</tr>
								);
							})
						) : (
							<tr>
								<td>Pihak Yang Hadir</td>
								<td />
								<td className="insert">:</td>
								<td align="center">Jabatan</td>
								<td className="insert">:</td>
							</tr>
						)}
					</tbody>
				</table>

				<table className="border">
					<colgroup>
						<col width={40} />
						<col width={'30%'} />
						<col />
					</colgroup>
					<thead>
						<tr>
							<th
								className="border"
								style={{ background: '#c0c0c0' }}
							>
								No
							</th>
							<th
								className="border"
								colSpan={2}
								style={{ background: '#c0c0c0' }}
							>
								Hasil Kunjungan
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								1
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Tujuan Kunjungan
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.visitPurpose}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								2
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Lokasi Kunjungan
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.visitSiteAddress}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								3
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Analisa Lokasi Kunjungan
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.visitLocationAnalysis}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								4
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Informasi Lainnya
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.additionalVisitInfo}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								5
							</td>
							<td
								style={{
									borderBottom: '1px solid',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								Verifikasi Kebutuhan Unit
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.unitNeedsVerification}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								6
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Verifikasi Unit yang sedang beroperasi
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.operatingUnitVerification}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								7
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Informasi Lainnya
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.additionalUnitInfo}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								8
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Verifikasi Aktivitas Usaha
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.businessActivityVerification}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								9
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Verifikasi Data Produksi vs Target
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.productionDataVsTargetVerification}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								10
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Penjelasan hambatan operasional
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.operationalConstraintsExplanation}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								11
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Informasi Lainnya
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.additionalBusinessActivityInfo}
							</td>
						</tr>
						<tr>
							<td
								className="border"
								align="center"
								style={{
									borderTop: 'none',
									verticalAlign: 'middle',
									height: 100,
								}}
							>
								12
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								Action Plan
							</td>
							<td
								style={{ borderBottom: '1px solid', verticalAlign: 'middle' }}
							>
								: {siteReport?.actionPlan}
							</td>
						</tr>
					</tbody>
				</table>

				<table>
					<colgroup>
						<col width={'20%'} />
						<col width={'20%'} />
						<col width={'20%'} />
						<col width={'20%'} />
						<col width={'20%'} />
					</colgroup>
					<tbody>
						<tr>
							<td colSpan={5} />
						</tr>

						<tr>
							<td>Tanggal</td>
							<td className="insert">: {siteSubmitDate}</td>
							<td colSpan={3} />
						</tr>

						<tr>
							<td>Nama Fleet Relation Manager</td>
							<td className="insert underline">
								: {mainInformation?.frm?.value}
							</td>
							<td />
							<td>Nama Fleet Business Dept Head</td>
							<td className="insert underline">
								: {mainInformation?.fbdh?.value}
							</td>
						</tr>
						<tr>
							<td
								style={{
									height: 100,
									verticalAlign: 'middle',
								}}
							>
								Tandatangan
							</td>
							<td className="insert underline"></td>
							<td />
							<td style={{ verticalAlign: 'middle' }}>Tandatangan</td>
							<td className="insert underline"></td>
						</tr>
					</tbody>
				</table>

				<header style={{ marginBlock: 20 }}>
					LAMPIRAN FOTO HASIL KUNJUNGAN
				</header>
				<Row
					gutter={20}
					justify="center"
				>
					{siteReport?.photos.map((data) => {
						return (
							<Col
								key={data.id}
								span={6}
							>
								<Img
									src={data.photo.url}
									preview={false}
									crossOrigin="anonymous"
								/>
							</Col>
						);
					})}
				</Row>
			</div>
		);
	};

	return (
		<FleetModal
			open={!!modal}
			width={'100%'}
			okButtonProps={{ hidden: true }}
			cancelButtonProps={{ hidden: true }}
			afterOpenChange={handleOpenChange}
			onCancel={() => onClose()}
		>
			{modal?.type === 'lk-office' && renderOffice()}
			{modal?.type === 'lk-site' && renderSite()}
		</FleetModal>
	);
};

export type GenerateLKPDFModalState = null | {
	type: 'lk-office' | 'lk-site';
	prospectId: string;
};

export const useGenerateLKPDFModal = () => {
	return useState<GenerateLKPDFModalState>(null);
};

export default GenerateLKPDFModal;
